import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Wallet, Receipt, BarChart3,
  Car, Gift, ChevronRight, Building2, Info, CreditCard, Landmark, PieChart,
  ArrowUpRight, ArrowDownRight, Calendar, X, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtMoney } from "@/components/DashboardIngresos";
import { fmtDate, fmtTime } from "@/lib/datetime";

const ORIGEN_COLORS = {
  estacionamiento: "#3B82F6", valet: "#22C55E", productos: "#8B5CF6", servicios: "#F59E0B",
};
const CAT_COLORS = {
  "Nómina y salarios": "#EF4444", "Insumos": "#F97316", "Mantenimiento": "#F59E0B",
  "Pago a proveedor": "#8B5CF6", "Otros": "#9CA3AF",
};
const METODO_COLORS = { Tarjeta: "#3B82F6", Efectivo: "#22C55E" };
const MOTIVO_COLORS = ["#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6", "#22C55E"];

const PERIODOS = [
  { value: "7d", label: "Últimos 7 días" },
  { value: "30d", label: "Últimos 30 días" },
  { value: "este_mes", label: "Este mes" },
  { value: "mes_anterior", label: "Mes anterior" },
  { value: "custom", label: "Personalizado" },
];
const COMPARACIONES = [
  { value: "none", label: "Sin comparación" },
  { value: "periodo_anterior", label: "Periodo anterior equivalente" },
  { value: "ano_anterior", label: "Mismo periodo del año anterior" },
];

const fmtShort = (n) => {
  const v = Math.round(Number(n || 0));
  if (Math.abs(v) >= 1000) return "$" + (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + "k";
  return "$" + v;
};
const fmtFecha = (iso) => {
  if (!iso) return "—";
  return fmtDate(iso);
};

/* ---------- Delta % vs periodo comparado ---------- */
function DeltaPill({ value, invert = false, comparadoLabel }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  const positivo = invert ? !up : up;
  const color = positivo ? "text-[#15803D]" : "text-[#DC2626]";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <p className={`mt-1.5 flex items-center gap-1 text-[11px] font-bold ${color}`}>
      <Icon className="h-3 w-3" strokeWidth={2.6} />
      {up ? "+" : ""}{value}% {comparadoLabel ? <span className="font-semibold text-[#A1A1AA]">vs {comparadoLabel}</span> : null}
    </p>
  );
}

/* ---------- Tarjeta KPI ---------- */
function KpiCard({ icon: Icon, tone, label, value, sub, delta, invertDelta, comparadoLabel, footer, testid }) {
  return (
    <div data-testid={testid}
      className="rounded-2xl border border-[#e4e4e7] bg-white p-4 shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: tone.bg }}>
          <Icon className="h-4.5 w-4.5" style={{ color: tone.fg }} strokeWidth={2.4} />
        </span>
        <span className="text-[11px] tracking-[0.08em] uppercase font-bold text-[#52525B] leading-tight">{label}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-black leading-none text-[#0A0A0A]" data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
      {sub && <p className="mt-1 text-[11px] font-semibold text-[#71717A]">{sub}</p>}
      <DeltaPill value={delta} invert={invertDelta} comparadoLabel={comparadoLabel} />
      {footer}
    </div>
  );
}

/* ---------- Dona genérica ---------- */
function Donut({ segments, total, centerLabel }) {
  const r = 46, c = 2 * Math.PI * r;
  let acc = 0;
  const segs = (segments || []).filter((s) => s.value > 0);
  return (
    <svg width="132" height="132" viewBox="0 0 132 132" className="shrink-0 -rotate-90">
      <circle cx="66" cy="66" r={r} fill="none" stroke="#EEF0F2" strokeWidth="14" />
      {segs.map((s, i) => {
        const pct = total > 0 ? s.value / total : 0;
        const len = pct * c;
        const el = (
          <circle key={i} cx="66" cy="66" r={r} fill="none" stroke={s.color} strokeWidth="14"
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-acc} strokeLinecap="butt" />
        );
        acc += len;
        return el;
      })}
      <text x="66" y="62" textAnchor="middle" className="fill-[#0A0A0A] rotate-90" style={{ fontSize: 15, fontWeight: 800, transformOrigin: "center" }}>{fmtShort(total)}</text>
      <text x="66" y="78" textAnchor="middle" className="fill-[#A1A1AA] rotate-90" style={{ fontSize: 9, transformOrigin: "center" }}>{centerLabel}</text>
    </svg>
  );
}

/* ---------- Gráfica de evolución financiera ---------- */
function EvolucionChart({ data }) {
  const chartData = (data || []).map((b) => ({
    name: b.label, Ingresos: b.ingresos, Gastos: b.gastos, "Resultado operativo": b.resultado,
  }));
  if (chartData.length === 0) {
    return <div className="flex items-center justify-center h-[260px] text-sm text-[#A1A1AA]">Sin movimientos en el periodo.</div>;
  }
  return (
    <div className="h-[260px] w-full" data-testid="hist-evolucion-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
        <LineChart data={chartData} margin={{ top: 10, right: 12, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F2" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} tickLine={false} axisLine={{ stroke: "#E4E4E7" }} />
          <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#71717A" }} tickLine={false} axisLine={false} width={48} />
          <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E4E4E7", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600 }} iconType="circle" />
          <Line type="monotone" dataKey="Ingresos" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="Gastos" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="Resultado operativo" stroke="#3B82F6" strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- Comparativo por sucursal ---------- */
function ComparativoSucursal({ rows }) {
  const [expanded, setExpanded] = useState(false);
  const [orderBy, setOrderBy] = useState("ingresos");
  const ordered = [...rows].sort((a, b) =>
    ((b[orderBy] || 0) - (a[orderBy] || 0)) || ((b.ingresos || 0) - (a.ingresos || 0)) || a.nombre.localeCompare(b.nombre));
  const visibles = expanded ? ordered : ordered.slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r.ingresos));
  return (
    <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
            <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Comparativo por sucursal</p>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-0.5">Ingresos, gastos y resultado operativo del periodo.</p>
        </div>
        <Select value={orderBy} onValueChange={setOrderBy}>
          <SelectTrigger data-testid="hist-suc-orderby" className="w-[210px] h-9 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ingresos">Ordenar por: Ingresos</SelectItem>
            <SelectItem value="gastos">Ordenar por: Gastos</SelectItem>
            <SelectItem value="resultado">Ordenar por: Resultado operativo</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#A1A1AA] py-6 text-center">Sin datos en el periodo.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hist-sucursal-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] border-b border-[#e4e4e7]">
                <th className="py-2 pr-2 font-bold">Sucursal</th>
                <th className="py-2 px-2 font-bold w-[34%]">Ingresos</th>
                <th className="py-2 px-2 font-bold text-right">Gastos</th>
                <th className="py-2 pl-2 font-bold text-right">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((s) => (
                <tr key={s.id_sucursal} data-testid={`hist-suc-row-${s.id_sucursal}`} className="border-b border-[#f4f4f5] last:border-0">
                  <td className="py-2.5 pr-2 font-semibold text-[#3F3F46] truncate max-w-[130px]">{s.nombre}</td>
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 rounded-full bg-[#3B82F6]" style={{ width: `${Math.max(6, (s.ingresos / max) * 100)}%` }} />
                      <span className="font-bold text-[#0A0A0A] tabular-nums shrink-0">{fmtMoney(s.ingresos)}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-right text-[#DC2626] font-semibold tabular-nums">{fmtMoney(s.gastos)}</td>
                  <td className={`py-2.5 pl-2 text-right font-black tabular-nums ${s.resultado >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>{fmtMoney(s.resultado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 5 && (
        <button data-testid="hist-ver-todas-suc-btn" onClick={() => setExpanded((v) => !v)}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] transition-colors">
          {expanded ? "Ver menos" : `Ver todas las sucursales (${rows.length})`} <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/* ---------- Barras horizontales de métodos de pago ---------- */
function MetodoBars({ metodos }) {
  if (!metodos || metodos.length === 0) return <p className="text-sm text-[#A1A1AA] py-8 text-center">Sin cobros en el periodo.</p>;
  const max = Math.max(1, ...metodos.map((m) => m.monto));
  return (
    <div className="space-y-4 mt-4">
      {metodos.map((m) => {
        const color = METODO_COLORS[m.metodo] || "#71717A";
        const Icon = m.metodo === "Tarjeta" ? CreditCard : Wallet;
        return (
          <div key={m.metodo} data-testid={`hist-metodo-${m.metodo}`}>
            <div className="flex items-center justify-between mb-1.5 text-sm">
              <span className="flex items-center gap-2 font-bold text-[#3F3F46]"><Icon className="h-4 w-4" style={{ color }} /> {m.metodo}</span>
              <span className="flex items-center gap-2"><span className="font-display font-black text-[#0A0A0A] tabular-nums">{fmtMoney(m.monto)}</span><span className="text-xs font-bold text-[#A1A1AA] w-9 text-right">{m.pct}%</span></span>
            </div>
            <div className="h-3 w-full rounded-full bg-[#EEF0F2] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.max(4, (m.monto / max) * 100)}%`, backgroundColor: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Panel lateral paginado (gastos / cortesías) ---------- */
function usePaginado(endpoint, params, open) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(params);

  const cargar = useCallback(async (p) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (params.id_sucursal && params.id_sucursal !== "all") qs.set("id_sucursal", params.id_sucursal);
      qs.set("preset", params.preset);
      if (params.preset === "custom") { qs.set("desde", params.desde); qs.set("hasta", params.hasta); }
      qs.set("page", String(p)); qs.set("page_size", "10");
      const { data } = await api.get(`${endpoint}?${qs.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [endpoint, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) { setPage(1); cargar(1); } }, [open, key, cargar]);
  const go = (p) => { setPage(p); cargar(p); };
  return { data, page, loading, go };
}

function Paginacion({ data, page, onGo, prefix }) {
  if (!data || data.total <= data.page_size) return null;
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  const desde = (page - 1) * data.page_size + 1;
  const hasta = Math.min(page * data.page_size, data.total);
  return (
    <div className="mt-4 flex items-center justify-between">
      <span className="text-xs text-[#A1A1AA]" data-testid={`${prefix}-page`}>{desde}–{hasta} de {data.total}</span>
      <div className="flex items-center gap-1">
        <button data-testid={`${prefix}-prev`} disabled={page <= 1} onClick={() => onGo(page - 1)}
          className="inline-flex items-center gap-1 rounded-lg border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] disabled:opacity-40 transition-colors">
          <ChevronLeft className="h-4 w-4" /> Anterior
        </button>
        <span className="px-2 text-sm font-bold text-[#52525B]">{page} / {totalPages}</span>
        <button data-testid={`${prefix}-next`} disabled={page >= totalPages} onClick={() => onGo(page + 1)}
          className="inline-flex items-center gap-1 rounded-lg border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] disabled:opacity-40 transition-colors">
          Siguiente <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function GastosPanel({ open, onOpenChange, params, summary, comparadoLabel }) {
  const { data, page, loading, go } = usePaginado("/dashboard/historial/gastos", params, open);
  const items = data?.items || [];
  const cats = summary?.gastos_categorias || [];
  const gd = summary?.deltas?.gastos;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="hist-gastos-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
          <SheetTitle className="flex items-center gap-2 font-display text-xl font-black text-[#0A0A0A]">
            <Receipt className="h-5 w-5 text-[#DC2626]" /> Detalle de gastos del periodo
          </SheetTitle>
          <SheetDescription>Gastos registrados en {summary?.periodo?.label || "el periodo"}.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-5">
          <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-5" data-testid="hist-gastos-total-card">
            <div className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-[#EF4444] flex items-center justify-center shrink-0"><Wallet className="h-5 w-5 text-white" /></span>
              <div>
                <p className="font-display text-3xl font-black text-[#0A0A0A] leading-none">{fmtMoney(summary?.gastos_total)}</p>
                <p className="text-xs font-semibold text-[#B91C1C] mt-1">Total de gastos del periodo</p>
              </div>
            </div>
            <DeltaPill value={gd} invert comparadoLabel={comparadoLabel} />
          </div>

          {cats.length > 0 && (
            <div>
              <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-2">Gastos por categoría</p>
              <div className="space-y-2">
                {cats.map((c) => (
                  <div key={c.categoria} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-[#3F3F46]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CAT_COLORS[c.categoria] || "#9CA3AF" }} /> {c.categoria}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-black text-[#0A0A0A] tabular-nums">{fmtMoney(c.monto)}</span>
                      <span className="text-xs font-bold text-[#A1A1AA] w-8 text-right">{c.pct}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-2">Detalle de gastos</p>
            {loading && !data ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
            ) : items.length === 0 ? (
              <p className="text-center text-sm text-[#A1A1AA] py-12">No hay gastos registrados en el periodo.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#e4e4e7]">
                <table className="w-full text-sm" data-testid="hist-gastos-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] bg-[#FAFAFA] border-b border-[#e4e4e7]">
                      <th className="px-3 py-2 font-bold">Sucursal</th>
                      <th className="px-3 py-2 font-bold">Fecha</th>
                      <th className="px-3 py-2 font-bold">Categoría</th>
                      <th className="px-3 py-2 font-bold">Concepto</th>
                      <th className="px-3 py-2 font-bold text-right">Importe</th>
                      <th className="px-3 py-2 font-bold">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} data-testid={`hist-gasto-row-${it.id}`} className="border-b border-[#f4f4f5] last:border-0">
                        <td className="px-3 py-2.5 font-semibold text-[#3F3F46]">{it.sucursal}</td>
                        <td className="px-3 py-2.5 text-[#52525B] tabular-nums">{fmtFecha(it.fecha)}</td>
                        <td className="px-3 py-2.5 text-[#3F3F46]">{it.categoria}</td>
                        <td className="px-3 py-2.5 text-[#3F3F46]">{it.concepto || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-[#0A0A0A] tabular-nums">{fmtMoney(it.monto)}</td>
                        <td className="px-3 py-2.5 text-[#52525B]">{it.usuario || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion data={data} page={page} onGo={go} prefix="hist-gastos" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CortesiasPanel({ open, onOpenChange, params, summary }) {
  const { data, page, loading, go } = usePaginado("/dashboard/historial/cortesias", params, open);
  const items = data?.items || [];
  const motivos = summary?.cortesias_motivos || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="hist-cortesias-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
          <SheetTitle className="flex items-center gap-2 font-display text-xl font-black text-[#0A0A0A]">
            <Gift className="h-5 w-5 text-[#7C3AED]" /> Detalle de cortesías del periodo
          </SheetTitle>
          <SheetDescription>Cortesías otorgadas en {summary?.periodo?.label || "el periodo"} (no afectan ingresos ni gastos).</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-[#7C3AED] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-[#5B21B6]">Total de cortesías: {summary?.kpis?.cortesias_conteo ?? 0}</p>
              <p className="text-xs text-[#7C3AED]">{fmtMoney(summary?.kpis?.cortesias_monto)} en valor económico (no suma a ingresos ni a gastos).</p>
            </div>
          </div>

          {motivos.length > 0 && (
            <div>
              <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-2">Cortesías por motivo</p>
              <div className="space-y-2">
                {motivos.map((m, i) => (
                  <div key={m.motivo} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-[#3F3F46]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: MOTIVO_COLORS[i % MOTIVO_COLORS.length] }} /> {m.motivo}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[#A1A1AA]">{m.conteo} cortesías</span>
                      <span className="font-black text-[#0A0A0A] tabular-nums">{fmtMoney(m.monto)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-2">Detalle de cortesías</p>
            {loading && !data ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
            ) : items.length === 0 ? (
              <p className="text-center text-sm text-[#A1A1AA] py-12">No hay cortesías en el periodo.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#e4e4e7]">
                <table className="w-full text-sm" data-testid="hist-cortesias-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] bg-[#FAFAFA] border-b border-[#e4e4e7]">
                      <th className="px-3 py-2 font-bold">Sucursal</th>
                      <th className="px-3 py-2 font-bold">Fecha</th>
                      <th className="px-3 py-2 font-bold">Folio</th>
                      <th className="px-3 py-2 font-bold">Tipo</th>
                      <th className="px-3 py-2 font-bold">Motivo</th>
                      <th className="px-3 py-2 font-bold text-right">Importe</th>
                      <th className="px-3 py-2 font-bold">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id_ticket} data-testid={`hist-cortesia-row-${it.id_ticket}`} className="border-b border-[#f4f4f5] last:border-0">
                        <td className="px-3 py-2.5 font-semibold text-[#3F3F46]">{it.sucursal}</td>
                        <td className="px-3 py-2.5 text-[#52525B] tabular-nums">{fmtFecha(it.fecha)}</td>
                        <td className="px-3 py-2.5 font-mono text-[#52525B]">{it.folio || "—"}</td>
                        <td className="px-3 py-2.5 text-[#3F3F46]">{it.tipo}</td>
                        <td className="px-3 py-2.5 text-[#3F3F46]">{it.motivo || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-[#0A0A0A] tabular-nums">{fmtMoney(it.importe)}</td>
                        <td className="px-3 py-2.5 text-[#52525B]">{it.usuario || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion data={data} page={page} onGo={go} prefix="hist-cortesias" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ---------- Tab principal ---------- */
export function HistorialTab({ sucursales }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const hace7 = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [sucFiltro, setSucFiltro] = useState("all");
  const [preset, setPreset] = useState("7d");
  const [comparar, setComparar] = useState("none");
  const [desde, setDesde] = useState(hace7);
  const [hasta, setHasta] = useState(hoy);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [gastosOpen, setGastosOpen] = useState(false);
  const [cortOpen, setCortOpen] = useState(false);

  const params = useMemo(() => ({ id_sucursal: sucFiltro, preset, desde, hasta }), [sucFiltro, preset, desde, hasta]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (sucFiltro !== "all") qs.set("id_sucursal", sucFiltro);
      qs.set("preset", preset);
      if (preset === "custom") { qs.set("desde", desde); qs.set("hasta", hasta); }
      qs.set("comparar", comparar);
      const { data } = await api.get(`/dashboard/historial?${qs.toString()}`);
      setData(data);
      setLastUpdate(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [sucFiltro, preset, comparar, desde, hasta]);

  // Carga BAJO DEMANDA: al abrir la pestaña (mount) y cuando cambian los filtros.
  useEffect(() => { cargar(); }, [sucFiltro, preset, comparar, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparadoLabel = data?.periodo_comparado?.label;
  const kpis = data?.kpis;
  const d = data?.deltas;
  const comparaActiva = comparar !== "none" && !!data?.comparativo;
  const margen = kpis && kpis.ingresos > 0 ? (kpis.resultado / kpis.ingresos * 100) : null;
  const margenComp = comparaActiva && data?.comparativo?.ingresos > 0 ? (data.comparativo.resultado / data.comparativo.ingresos * 100) : null;
  const margenPP = (margen !== null && margenComp !== null) ? (margen - margenComp) : null;
  const margenFooter = (
    <div className="mt-2" data-testid="hist-margen">
      <p className="text-[11px] font-bold text-[#6D28D9]">
        Margen operativo: {margen === null ? "—" : `${margen.toFixed(1)}%`}
        {margenPP !== null && (
          <span className={margenPP >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}> ({margenPP >= 0 ? "+" : ""}{margenPP.toFixed(1)} pp)</span>
        )}
      </p>
      <p className="text-[10px] font-semibold text-[#A1A1AA] mt-0.5">Calculado con los gastos registrados en ParkNova.</p>
    </div>
  );

  const origenSegments = (data?.origen || []).map((o) => ({ value: o.monto, color: ORIGEN_COLORS[o.key] }));
  const gastosSegments = (data?.gastos_categorias || []).map((c) => ({ value: c.monto, color: CAT_COLORS[c.categoria] || "#9CA3AF" }));

  return (
    <div className="space-y-6" data-testid="historial-tab-root">
      {/* Controles */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wide font-bold text-[#A1A1AA] mb-1">Sucursal</label>
            <Select value={sucFiltro} onValueChange={setSucFiltro}>
              <SelectTrigger data-testid="hist-sucursal-select" className="w-[200px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las sucursales</SelectItem>
                {(sucursales || []).map((s) => <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide font-bold text-[#A1A1AA] mb-1">Periodo</label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="hist-periodo-select" className="w-[180px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODOS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wide font-bold text-[#A1A1AA] mb-1">Desde</label>
                <Input type="date" data-testid="hist-desde-input" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} className="bg-white w-[150px]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide font-bold text-[#A1A1AA] mb-1">Hasta</label>
                <Input type="date" data-testid="hist-hasta-input" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} className="bg-white w-[150px]" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wide font-bold text-[#A1A1AA] mb-1">Comparar contra</label>
            <Select value={comparar} onValueChange={setComparar}>
              <SelectTrigger data-testid="hist-comparar-select" className="w-[240px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPARACIONES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline-flex items-center gap-1.5 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-3 h-9 text-xs font-bold text-[#1E3A8A]">
            <Info className="h-3.5 w-3.5" /> Consulta bajo demanda
          </span>
          <button data-testid="hist-actualizar-btn" onClick={cargar} disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-bold text-white hover:bg-[#27272A] transition-colors disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
        </div>
      </div>
      {lastUpdate && (
        <p className="text-[11px] text-[#A1A1AA] -mt-3" data-testid="hist-last-update">
          Última consulta {fmtTime(lastUpdate)}
          {data?.periodo?.label ? ` · ${data.periodo.label}` : ""}
        </p>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-24" data-testid="hist-loading"><Loader2 className="h-8 w-8 animate-spin text-[#B45309]" /></div>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <KpiCard testid="hist-kpi-ingresos" icon={TrendingUp} tone={{ bg: "#DCFCE7", fg: "#16A34A" }}
              label="Ingresos del periodo" value={fmtMoney(kpis.ingresos)} delta={comparaActiva ? d?.ingresos : null} comparadoLabel={comparadoLabel} />
            <KpiCard testid="hist-kpi-gastos" icon={Receipt} tone={{ bg: "#FEE2E2", fg: "#DC2626" }}
              label="Gastos del periodo" value={fmtMoney(kpis.gastos)} delta={comparaActiva ? d?.gastos : null} invertDelta comparadoLabel={comparadoLabel} />
            <KpiCard testid="hist-kpi-resultado" icon={BarChart3} tone={{ bg: "#EDE9FE", fg: "#7C3AED" }}
              label="Resultado operativo" value={fmtMoney(kpis.resultado)} delta={comparaActiva ? d?.resultado : null} comparadoLabel={comparadoLabel} footer={margenFooter} />
            <KpiCard testid="hist-kpi-operaciones" icon={Car} tone={{ bg: "#DBEAFE", fg: "#2563EB" }}
              label="Operaciones cobradas" value={kpis.operaciones.toLocaleString("es-MX")} sub="operaciones en el periodo" delta={comparaActiva ? d?.operaciones : null} comparadoLabel={comparadoLabel} />
            <KpiCard testid="hist-kpi-cortesias" icon={Gift} tone={{ bg: "#FEF3C7", fg: "#B45309" }}
              label="Cortesías del periodo" value={`${kpis.cortesias_conteo} operaciones`} sub={`${fmtMoney(kpis.cortesias_monto)} en cortesías`} delta={comparaActiva ? d?.cortesias_monto : null} comparadoLabel={comparadoLabel} />
          </div>

          {/* Evolución + Comparativo por sucursal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
                <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Evolución financiera</p>
              </div>
              <p className="text-xs text-[#A1A1AA] mb-3">Ingresos, gastos y resultado operativo{comparaActiva ? " del periodo seleccionado" : ""}.</p>
              <EvolucionChart data={data.evolucion} />
            </div>
            <ComparativoSucursal rows={data.por_sucursal || []} />
          </div>

          {/* Origen + Métodos + Gastos por categoría */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm" data-testid="hist-origen">
              <div className="flex items-center gap-2 mb-1">
                <PieChart className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
                <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Origen de ingresos del periodo</p>
              </div>
              <p className="text-xs text-[#A1A1AA] mb-3">Distribución por línea de negocio.</p>
              <div className="flex items-center gap-4">
                <Donut segments={origenSegments} total={kpis.ingresos} centerLabel="total" />
                <div className="flex-1 space-y-2 min-w-0">
                  {(data.origen || []).map((o) => (
                    <div key={o.key} className="flex items-center justify-between gap-2" data-testid={`hist-origen-${o.key}`}>
                      <span className="flex items-center gap-2 text-xs font-semibold text-[#3F3F46] truncate">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: ORIGEN_COLORS[o.key] }} /> {o.label}
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-black text-[#0A0A0A] tabular-nums text-xs">{fmtMoney(o.monto)}</span>
                        <span className="text-[11px] font-bold text-[#A1A1AA] w-7 text-right">{o.pct}%</span>
                      </span>
                    </div>
                  ))}
                  {data.recargas_disponible && (
                    <div className="flex items-center gap-2 text-[11px] text-[#A1A1AA] pt-1 border-t border-dashed border-[#e4e4e7]">
                      <Landmark className="h-3.5 w-3.5" /> Recargas y pago de servicios (módulo habilitado)
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm" data-testid="hist-metodos">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
                <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Métodos de pago del periodo</p>
              </div>
              <p className="text-xs text-[#A1A1AA]">Solo métodos de cobro en la operación.</p>
              <MetodoBars metodos={data.metodos_pago} />
            </div>

            <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm" data-testid="hist-gastos-categorias">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
                  <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Gastos por categoría</p>
                </div>
                <button data-testid="hist-ver-gastos-btn" onClick={() => setGastosOpen(true)} className="inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:underline">
                  Ver detalle <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-xs text-[#A1A1AA] mb-3">Distribución del total de gastos.</p>
              {(data.gastos_categorias || []).length === 0 ? (
                <p className="text-sm text-[#A1A1AA] py-8 text-center">Sin gastos en el periodo.</p>
              ) : (
                <div className="flex items-center gap-4">
                  <Donut segments={gastosSegments} total={data.gastos_total} centerLabel="total" />
                  <div className="flex-1 space-y-2 min-w-0">
                    {data.gastos_categorias.map((c) => (
                      <div key={c.categoria} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-xs font-semibold text-[#3F3F46] truncate">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: CAT_COLORS[c.categoria] || "#9CA3AF" }} /> {c.categoria}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="font-black text-[#0A0A0A] tabular-nums text-xs">{fmtMoney(c.monto)}</span>
                          <span className="text-[11px] font-bold text-[#A1A1AA] w-7 text-right">{c.pct}%</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cortesías por motivo */}
          <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm" data-testid="hist-motivos">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-[#7C3AED]" strokeWidth={2.3} />
                <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Cortesías por motivo</p>
              </div>
              <button data-testid="hist-ver-cortesias-btn" onClick={() => setCortOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] transition-colors">
                Ver detalle de cortesías <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-[#A1A1AA] mb-4">Valor y cantidad de cortesías otorgadas en el periodo (no afectan ingresos ni gastos).</p>
            {(data.cortesias_motivos || []).length === 0 ? (
              <p className="text-sm text-[#A1A1AA] py-4">Sin cortesías en el periodo.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {data.cortesias_motivos.map((m, i) => (
                  <div key={m.motivo} className="rounded-xl border border-[#f4f4f5] bg-[#FAFAFA] p-3" data-testid={`hist-motivo-${i}`}>
                    <span className="flex items-center gap-1.5 text-xs font-bold text-[#52525B]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: MOTIVO_COLORS[i % MOTIVO_COLORS.length] }} /> {m.motivo}
                    </span>
                    <p className="mt-1.5 font-display text-2xl font-black text-[#0A0A0A] leading-none">{fmtMoney(m.monto)}</p>
                    <p className="text-[11px] text-[#71717A] mt-0.5">{m.conteo} cortesías</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <GastosPanel open={gastosOpen} onOpenChange={setGastosOpen} params={params} summary={data} comparadoLabel={comparadoLabel} />
      <CortesiasPanel open={cortOpen} onOpenChange={setCortOpen} params={params} summary={data} />
    </div>
  );
}
